import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Collapse,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Segmented,
    Skeleton,
    Spin,
    Switch,
    Tooltip,
    message,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SortAscendingOutlined,
    CheckCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { CircleHelp } from "lucide-react";
import faqApi from "../../api/faqApi";
import profileApi from "../../api/profileApi";
import "../../components/AddUserModal/style.css";

const { TextArea } = Input;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const pill = "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold";

export default function FAQPage() {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [isAdmin, setIsAdmin] = useState(false);
    const [scope, setScope] = useState("active");

    useEffect(() => {
        (async () => {
            await checkRole();
            await fetchFaqs();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        fetchFaqs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, isAdmin]);

    const checkRole = async () => {
        try {
            const profile = await profileApi.myProfile();
            setIsAdmin(profile.role === "admin" || profile.role === "ADMIN");
        } catch (error) {
            console.error("Ошибка при проверке роли:", error);
        }
    };

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const data = isAdmin && scope === "all" ? await faqApi.getAllFaqs() : await faqApi.getFaqs();
            const sorted = Array.isArray(data) ? [...data].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
            setFaqs(sorted);
        } catch (error) {
            console.error("Ошибка при загрузке FAQ:", error);
            message.error("Не удалось загрузить FAQ");
            setFaqs([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredFaqs = useMemo(() => {
        if (!searchQuery.trim()) return faqs;
        const q = searchQuery.toLowerCase();
        return faqs.filter(
            (f) =>
                (f.question || "").toLowerCase().includes(q) || (f.answer || "").toLowerCase().includes(q)
        );
    }, [faqs, searchQuery]);

    const openCreateModal = () => {
        setEditingFaq(null);
        form.resetFields();
        form.setFieldsValue({
            question: "",
            answer: "",
            order: (faqs?.length || 0) + 1,
            isActive: true,
        });
        setIsModalOpen(true);
    };

    const openEditModal = (faq) => {
        setEditingFaq(faq);
        form.setFieldsValue({
            question: faq.question,
            answer: faq.answer,
            order: faq.order ?? 0,
            isActive: faq.isActive !== undefined ? faq.isActive : true,
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingFaq(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            if (editingFaq) {
                await faqApi.updateFaq(editingFaq.id, {
                    question: values.question,
                    answer: values.answer,
                    order: values.order,
                    isActive: values.isActive !== undefined ? values.isActive : true,
                });
                message.success("FAQ обновлён");
            } else {
                await faqApi.createFaq({
                    question: values.question,
                    answer: values.answer,
                    order: values.order,
                });
                message.success("FAQ создан");
            }

            closeModal();
            await fetchFaqs();
        } catch (error) {
            if (error?.errorFields) return;
            console.error("Ошибка при сохранении FAQ:", error);
            const errorMessage = error.response?.data?.message || error.message || "Не удалось сохранить FAQ";
            message.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await faqApi.deleteFaq(id);
            message.success("FAQ удалён");
            await fetchFaqs();
        } catch (error) {
            console.error("Ошибка при удалении FAQ:", error);
            message.error("Не удалось удалить FAQ");
        }
    };

    const headerStats = useMemo(() => {
        const total = faqs.length;
        const active = faqs.filter((f) => f.isActive !== false).length;
        const inactive = total - active;
        return { total, active, inactive };
    }, [faqs]);

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] min-h-0 flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <CircleHelp className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Часто задаваемые вопросы
                            </h1>
                            <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-white/45">
                                Управление FAQ для бота: порядок, активность и тексты.
                            </p>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className={cn(pill, "border-white/12 bg-white/[0.06] text-white/65")}>
                                    <SortAscendingOutlined className="text-white/50" />
                                    order
                                </span>
                                <span className={cn(pill, "border-emerald-400/30 bg-emerald-500/12 text-emerald-100")}>
                                    <CheckCircleOutlined />
                                    активных: {headerStats.active}
                                </span>
                                <span className={cn(pill, "border-amber-400/30 bg-amber-500/12 text-amber-100")}>
                                    <StopOutlined />
                                    неактивных: {headerStats.inactive}
                                </span>
                                <span className={cn(pill, "border-sky-400/30 bg-sky-500/12 text-sky-100")}>
                                    всего: {headerStats.total}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-col gap-3 sm:items-end">
                        {isAdmin && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateModal}
                                className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                            >
                                Создать FAQ
                            </Button>
                        )}
                        {isAdmin && (
                            <div className="flex flex-col gap-2 sm:items-end">
                                <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    Показывать
                                </span>
                                <div
                                    className={cn(
                                        "rounded-xl border border-white/10 bg-white/[0.05] p-1",
                                        "[&_.ant-segmented]:!bg-transparent",
                                        "[&_.ant-segmented-item]:!text-white/55",
                                        "[&_.ant-segmented-item-selected]:!bg-white/[0.12] [&_.ant-segmented-item-selected]:!text-white",
                                        "[&_.ant-segmented-thumb]:!bg-white/[0.1]"
                                    )}
                                >
                                    <Segmented
                                        value={scope}
                                        onChange={(v) => setScope(v)}
                                        options={[
                                            { label: "Активные", value: "active" },
                                            { label: "Все", value: "all" },
                                        ]}
                                        size="middle"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className={cn(shellClass, "shrink-0 p-4 sm:px-5 sm:py-4")}>
                <Input
                    allowClear
                    size="large"
                    placeholder="Поиск по вопросам и ответам…"
                    prefix={<SearchOutlined className="text-white/35" />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="!rounded-xl !border-white/10 !bg-white/[0.06] py-2.5 text-white/90 placeholder:!text-white/35"
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/40">
                    <span>Ищет по полям question и answer</span>
                    <span className="text-white/55">{filteredFaqs.length} результатов</span>
                </div>
            </section>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading ? (
                    <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-5">
                        <div className="mb-4 flex items-center gap-3">
                            <Spin className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                            <span className="text-[13px] text-white/55">Загрузка FAQ…</span>
                        </div>
                        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                                >
                                    <Skeleton active title={{ width: "65%" }} paragraph={{ rows: 2 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : filteredFaqs.length === 0 ? (
                    <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center p-8">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span className="text-[13px] text-white/45">
                                    {searchQuery ? "Ничего не найдено" : "FAQ пока нет"}
                                </span>
                            }
                        />
                        {isAdmin && !searchQuery && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateModal}
                                className="mt-4 !rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                            >
                                Создать первый FAQ
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="faq-collapse-dark min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
                        <Collapse
                            activeKey={expandedKeys}
                            onChange={(keys) => setExpandedKeys(keys)}
                            bordered={false}
                            expandIconPosition="end"
                            className="!border-0 !bg-transparent [&_.ant-collapse-item]:!border-0"
                            items={filteredFaqs.map((faq, index) => {
                                const isActive = faq.isActive !== false;
                                const views = faq.views;

                                const header = (
                                    <div className="flex w-full items-start justify-between gap-4 pr-1">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge
                                                    status={isActive ? "success" : "default"}
                                                    text={
                                                        <span className="break-words text-[15px] font-semibold text-white/90 sm:text-base">
                                                            {faq.question}
                                                        </span>
                                                    }
                                                />
                                                {!isActive && (
                                                    <span
                                                        className={cn(
                                                            pill,
                                                            "border-white/15 bg-white/[0.08] text-white/50"
                                                        )}
                                                    >
                                                        Неактивен
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-white/45">
                                                {typeof faq.order !== "undefined" && (
                                                    <span className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.05] px-2 py-1">
                                                        <SortAscendingOutlined />
                                                        order: {faq.order}
                                                    </span>
                                                )}
                                                {typeof views !== "undefined" && (
                                                    <span className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.05] px-2 py-1">
                                                        <EyeOutlined />
                                                        {views}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {isAdmin && (
                                            <div
                                                className="flex shrink-0 items-center gap-0.5"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Tooltip title="Редактировать">
                                                    <Button
                                                        type="text"
                                                        icon={<EditOutlined />}
                                                        onClick={() => openEditModal(faq)}
                                                        className="!text-emerald-300 hover:!bg-white/10 hover:!text-emerald-200"
                                                    />
                                                </Tooltip>
                                                <Popconfirm
                                                    title="Удалить FAQ?"
                                                    description="Удалить этот вопрос безвозвратно?"
                                                    onConfirm={() => handleDelete(faq.id)}
                                                    okText="Да"
                                                    cancelText="Нет"
                                                    okButtonProps={{ danger: true }}
                                                    overlayClassName="[&_.ant-popconfirm-inner]:!rounded-xl [&_.ant-popconfirm-inner]:!border [&_.ant-popconfirm-inner]:!border-white/10 [&_.ant-popconfirm-inner]:!bg-[#0d111c] [&_.ant-popconfirm-message]:!text-white/85"
                                                >
                                                    <Tooltip title="Удалить">
                                                        <Button
                                                            type="text"
                                                            icon={<DeleteOutlined />}
                                                            danger
                                                            className="!text-rose-400 hover:!bg-rose-500/10"
                                                        />
                                                    </Tooltip>
                                                </Popconfirm>
                                            </div>
                                        )}
                                    </div>
                                );

                                return {
                                    key: faq.id,
                                    label: (
                                        <motion.div
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.22, delay: index * 0.02 }}
                                            className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-4 sm:px-5"
                                        >
                                            {header}
                                        </motion.div>
                                    ),
                                    children: (
                                        <div className="mt-2 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-4 sm:px-5 sm:py-5">
                                            <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-white/80">
                                                {faq.answer}
                                            </div>
                                            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-white/[0.06] pt-3 text-[11px] text-white/40">
                                                <div className="flex flex-wrap gap-3">
                                                    {typeof faq.order !== "undefined" && <span>Порядок: {faq.order}</span>}
                                                    {typeof faq.views !== "undefined" && (
                                                        <span>Просмотров: {faq.views}</span>
                                                    )}
                                                </div>
                                                {isAdmin && editingFaq?.id === faq.id ? (
                                                    <span className="text-emerald-300/90">Редактируется…</span>
                                                ) : null}
                                            </div>
                                        </div>
                                    ),
                                    classNames: { body: "!bg-transparent !p-0" },
                                    style: { marginBottom: 10, border: "none", background: "transparent" },
                                };
                            })}
                        />
                    </div>
                )}
            </section>

            <Modal
                title={
                    <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.08]">
                            {editingFaq ? (
                                <EditOutlined className="text-emerald-300" />
                            ) : (
                                <PlusOutlined className="text-emerald-300" />
                            )}
                        </span>
                        <span className="text-[15px] font-semibold text-white/90">
                            {editingFaq ? "Редактировать FAQ" : "Создать FAQ"}
                        </span>
                    </div>
                }
                open={isModalOpen}
                onOk={handleSubmit}
                onCancel={closeModal}
                okText={saving ? "Сохранение…" : "Сохранить"}
                cancelText="Отмена"
                confirmLoading={saving}
                width={720}
                centered
                className="custom-modal"
                maskStyle={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
            >
                <Form form={form} layout="vertical" className="mt-2 text-white">
                    <Form.Item
                        name="question"
                        label="Вопрос"
                        rules={[{ required: true, message: "Введите вопрос" }]}
                    >
                        <Input
                            placeholder="Например: Как подключить кошелёк?"
                            className="!rounded-xl !border-white/10 !bg-white/[0.06] text-white/90 placeholder:!text-white/35"
                        />
                    </Form.Item>

                    <Form.Item
                        name="answer"
                        label="Ответ"
                        rules={[{ required: true, message: "Введите ответ" }]}
                    >
                        <TextArea
                            rows={7}
                            placeholder="Подробный ответ (можно с переносами строк)"
                            className="!rounded-xl !border-white/10 !bg-white/[0.06] text-white/90 placeholder:!text-white/35"
                        />
                    </Form.Item>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Form.Item
                            name="order"
                            label="Порядок"
                            rules={[{ required: true, message: "Введите порядок" }]}
                        >
                            <InputNumber
                                min={0}
                                className="w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10 [&_.ant-input-number-handler]:!border-white/10"
                                controls
                            />
                        </Form.Item>

                        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-[13px] font-medium text-white/85">Активность</div>
                                    <div className="mt-0.5 text-[11px] text-white/40">
                                        Неактивные не показываются в боте.
                                    </div>
                                </div>
                                <Form.Item name="isActive" valuePropName="checked" noStyle>
                                    <Switch className="[&_.ant-switch-checked]:!bg-emerald-500" />
                                </Form.Item>
                            </div>
                            {!editingFaq && (
                                <p className="mt-2 text-[11px] text-white/35">
                                    При создании по умолчанию запись активна.
                                </p>
                            )}
                        </div>
                    </div>

                    <p className="text-[11px] text-white/35">
                        Короткий вопрос и развёрнутый ответ обычно лучше смотрятся в боте.
                    </p>
                </Form>
            </Modal>
        </div>
    );
}
